import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { LateFeeNote } from "@/components/site/LateFeeNote";
import { FeesByTerm } from "@/components/site/FeesByTerm";
import { sitePricingQuery } from "@/lib/pricing-query";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "@/components/site/Page";
import { OnboardingNote } from "@/components/site/Highlights";
import { currentSchoolYearLabel } from "@/lib/school-year";
import { safeLoader } from "@/lib/safe-loader";

const title = `Tarifas del curso ${currentSchoolYearLabel()} | SIS Estepona`;
const description =
  "Tarifas por periodo para preescolar, primaria, estudios a distancia, actividades extraescolares y la academia de fútbol del Malmö FF en Estepona.";

export const Route = createFileRoute("/es/tarifas")({
  loader: ({ context }) => safeLoader("es.fees", () => context.queryClient.ensureQueryData(sitePricingQuery), { pricing: null, stale: false, fetchedAt: null, error: "unavailable", termSettings: null }),
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/es/tarifas") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "es_ES" },
      ...ogImage(),
    ],
    links: seoLinks("/es/tarifas"),
  }),
  component: Tarifas,
});


function Tarifas() {
  const pricingData = Route.useLoaderData();
  return (
    <>
      <PageHero
        eyebrow="Tarifas"
        title="Tarifas y planes de pago"
        intro="La cuota se abona por semestre y por adelantado. Se puede ofrecer el pago a plazos previo acuerdo, en cuyo caso se indica en el contrato. También ofrecemos descuentos para familias numerosas. No duden en contactarnos si tienen preguntas sobre las cuotas."
      />

      <section className="mx-auto max-w-4xl px-5 pt-8">
        <p className="rounded-full border border-primary/15 bg-primary/5 px-5 py-3 text-sm text-foreground/75 shadow-soft">Estos precios se aplican a HT26, VT27, HT27 y VT28 y se publicaron el 28 de septiembre de 2026. Los nuevos precios de un semestre se publican aquí a más tardar el 10 de enero (semestre de otoño) y el 1 de agosto (semestre de primavera).</p>
      </section>
      <section className="mx-auto max-w-4xl px-5 py-8 sm:py-10">
        <div className="rounded-lg border border-border bg-card p-5 shadow-soft sm:p-8">
        <h2 className="text-2xl font-semibold">Matrícula y descuentos</h2>
        <p className="mt-2 text-sm text-muted-foreground italic">
          La cuota de matrícula también se denomina a veces cuota de inscripción: es la misma cuota.
        </p>
        <div className="mt-6 rounded-md border border-border bg-muted/40 p-5 text-sm leading-relaxed text-foreground/85"><p>La matrícula se paga una vez por alumno y aparece en cada periodo más abajo.</p><p className="mt-3">La estancia corta tiene una matrícula reducida, válida para dos periodos del mismo curso escolar.</p></div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2"><div className="rounded-md border border-primary/15 bg-primary/5 p-5"><strong className="text-2xl text-primary">20 %</strong><p className="mt-1 text-sm text-foreground/75">Descuento familiar para el tercer hijo, aplicado a la tarifa más baja.</p></div><div className="rounded-md border border-primary/15 bg-primary/5 p-5"><strong className="text-2xl text-primary">50 %</strong><p className="mt-1 text-sm text-foreground/75">Descuento familiar para el cuarto hijo, aplicado a la tarifa más baja.</p></div></div>
        </div>

        <h2 className="mt-14 text-2xl font-semibold">Tarifas por periodo</h2>

        <FeesByTerm locale="es" initialData={pricingData} />

        <div className="mt-10 space-y-2 text-sm leading-relaxed text-muted-foreground">
          <LateFeeNote locale="es" />
          <p>
            Las estancias cortas no pueden combinar semanas y meses. Se pueden reservar como máximo
            dos semanas por periodo; en caso contrario debe reservarse un mes o un periodo completo.
            Como máximo pueden reservarse dos meses por periodo.
          </p>
          <p>
            Si el alumno participa en la academia de fútbol del Malmö FF, el colegio se encarga de todo el proceso de solicitud y de las condiciones de participación. La academia de fútbol del Malmö FF requiere escolarización mediante Sofia Distans y una
            plaza en Swedish International School.
          </p>
        </div>

        <OnboardingNote className="mt-12 bg-card" locale="es" />

        <div className="mt-8 rounded-xl border border-border bg-sand p-6 sm:p-7">
          <h3 className="text-lg font-semibold">Ventajas con Villa Padierna</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Swedish International School colabora con Villa Padierna. Cuando la colaboración lo
            permite, nuestras familias pueden acceder a una tarjeta de ventajas con descuentos y
            privilegios en la zona.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Por ejemplo, puede incluir:
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-relaxed text-muted-foreground">
            <li>Pádel y tenis</li>
            <li>Golf</li>
            <li>Restaurantes de Villa Padierna</li>
          </ul>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            Las ventajas se ofrecen en el marco de la colaboración y pueden cambiar con el tiempo.
            No podemos garantizar su disponibilidad ni su contenido de antemano.
          </p>
        </div>
      </section>

      <Prose>
        <h2>Qué incluye la tarifa</h2>
        <p>
          La tarifa por periodo cubre la enseñanza según el currículo sueco con personal sueco­hablante,
          las instalaciones y las actividades habituales del colegio. La
          cuota de matrícula se abona una sola vez por alumno en el momento de la admisión. Los
          servicios opcionales, como las actividades extraescolares, la academia de fútbol o el programa
          de pádel y tenis, quedan fuera de la tarifa y se indican más arriba.
        </p>

        <h2>Pago y condiciones</h2>
        <p>
          La cuota escolar debe estar abonada antes de que el alumno empiece en el colegio. La cuota del semestre de primavera vence el 25 de octubre y la del semestre de otoño el 1 de mayo. Si el alumno empieza durante un semestre en curso, la cuota completa del semestre se abona en un plazo de tres días hábiles desde la factura y antes del primer día de clase. Las fechas de cada semestre figuran en{" "}
          <a className="text-primary link-quiet" href="/es/calendario-escolar">el calendario escolar</a>.
        </p>

        <h2>Descuento familiar y estancias cortas</h2>
        <p>
          Las familias con varios hijos en el colegio reciben el descuento indicado, calculado sobre la
          tarifa más baja. Para familias que pasan solo parte del año en España existe la matrícula de
          estancia corta, con una cuota de matrícula reducida y una tarifa según la duración. El{" "}
          <a className="text-primary link-quiet" href="/es/solicitud">formulario de solicitud</a>{" "}
          calcula el total a medida que eliges etapa, periodo y servicios.
        </p>
      </Prose>
      <FaqBlock items={faqFor("avgifter", "es")} locale="es" />


      <CtaBand
        title="¿Dudas sobre las tarifas?"
        text="Contáctenos y repasaremos las cuotas para su familia."
        locale="es"
      />
      <div className="h-20" />
    </>
  );
}
